import { useCallback, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import type { ConceptRefineRequest, ConceptRefineResponse } from "@/types/concept";

// ── Types ────────────────────────────────────────────────────────────────────

export interface RefineProposal {
  name: string;
  abstract: string;
  keywords: string[];
  rationale: string;
  based_on_concept_updated_at: string;
}

export type RescoreStatus =
  | "idle"
  | "started"
  | "progress"
  | "complete"
  | "error";

export interface RescoreProgress {
  rescored: number;
  total: number;
  changed: number;
  failed?: number;
  /** On complete: "scored" ran inline; "batched" was handed to a background
   * batch (still running); "deferred" waited behind one (nothing ran). Absent
   * from older backends — treat as "scored". */
  outcome?: "scored" | "batched" | "deferred";
  status: RescoreStatus;
  errorMessage?: string;
}

// ── Legacy hook (used by concept creation interview) ─────────────────────────

export function useConceptRefine() {
  return useMutation({
    mutationFn: (data: ConceptRefineRequest) =>
      apiClient
        .post("api/concepts/refine", { json: data, timeout: 120_000 })
        .json<ConceptRefineResponse>(),
  });
}

// ── useRefineConceptMutation ─────────────────────────────────────────────────

export function useRefineConceptMutation(conceptId: string) {
  return useMutation<RefineProposal>({
    mutationFn: () =>
      apiClient
        .post(`api/concepts/${conceptId}/ai-refine`, { timeout: 120_000 })
        .json<RefineProposal>(),
  });
}

// ── useRescoreMutation ───────────────────────────────────────────────────────

export function useRescoreMutation(conceptId: string) {
  return useMutation<{ job_id: string }>({
    mutationFn: () =>
      apiClient
        .post(`api/concepts/${conceptId}/rescore`, { timeout: 120_000 })
        .json<{ job_id: string }>(),
  });
}

// ── useRescoreProgress ───────────────────────────────────────────────────────

const INITIAL_PROGRESS: RescoreProgress = {
  rescored: 0,
  total: 0,
  changed: 0,
  status: "idle",
};

export function useRescoreProgress(conceptId: string) {
  const [progress, setProgress] = useState<RescoreProgress>(INITIAL_PROGRESS);
  const controllerRef = useRef<AbortController | null>(null);

  const subscribe = useCallback(
    async (jobId: string) => {
      // Abort any existing subscription
      controllerRef.current?.abort();

      const controller = new AbortController();
      controllerRef.current = controller;

      setProgress({ ...INITIAL_PROGRESS, status: "started" });

      const baseUrl =
        import.meta.env.VITE_API_URL ?? "http://localhost:8000";
      const url = `${baseUrl}/api/concepts/${conceptId}/rescore/${jobId}/stream`;

      try {
        const token = await getAuthToken();

        for await (const event of streamSSE(
          url,
          { Authorization: `Bearer ${token}` },
          controller.signal,
        )) {
          if (event.event === "started") {
            const data = JSON.parse(event.data) as { total_papers: number };
            setProgress((prev) => ({
              ...prev,
              total: data.total_papers,
              status: "started",
            }));
          } else if (event.event === "progress") {
            const data = JSON.parse(event.data) as {
              rescored: number;
              total: number;
              changed: number;
            };
            setProgress({
              rescored: data.rescored,
              total: data.total,
              changed: data.changed,
              status: "progress",
            });
          } else if (event.event === "complete") {
            const data = JSON.parse(event.data) as {
              total_rescored: number;
              changed: number;
              outcome?: RescoreProgress["outcome"];
            };
            setProgress({
              rescored: data.total_rescored,
              total: data.total_rescored,
              changed: data.changed,
              outcome: data.outcome,
              status: "complete",
            });
            return;
          } else if (event.event === "error") {
            const data = JSON.parse(event.data) as { error: string };
            setProgress((prev) => ({
              ...prev,
              status: "error",
              errorMessage: data.error,
            }));
            return;
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setProgress((prev) => ({
          ...prev,
          status: "error",
          errorMessage: err instanceof Error ? err.message : "SSE stream failed",
        }));
      }
    },
    [conceptId],
  );

  const unsubscribe = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  return { progress, subscribe, unsubscribe };
}
